import { Component, EventEmitter, Injectable, Output, Input } from '@angular/core';
import { DataGridSorter, DataGridColumn, DataGridEventInformation, DataGridSortInformation } from './datagrid-core.component';
import { TransactionalInformation } from '../../model/generic/transactionalInformation';
import { SecurityService } from '../../services/security.service';

// https://www.codeproject.com/Articles/1116876/Developing-An-Angular-Application-With-TypeScript

@Component({
    selector: 'app-datagrid',
    styleUrls: ['./datagrid.component.css'],
    templateUrl: './datagrid.component.html',
    standalone: false
})
@Injectable()
export class DataGridComponent {

  @Output() datagridEvent: EventEmitter<{}>;
  @Input() pageSize: number;
  @Input() columns: Array<DataGridColumn>;
  @Input() rows: Array<any>;

  public sorter: DataGridSorter;
  public pageSizes = [];
  public sortColumn: string;
  public sortDesending: boolean;
  public sortAscending: boolean;

  public disableFirstPageButton: boolean;
  public disablePreviousPageButton: boolean;
  public disableNextPageButton: boolean;
  public disableLastPageButton: boolean;
  public pageSizeForGrid: number;

  public currentPageNumber: number;
  public totalRows: number;
  public totalPages: number;
  public itemNumberBegin: number;
  public itemNumberEnd: number;

  public executeOperation: boolean;

  private workingList: any[];

  constructor(private securityService: SecurityService) {
    this.sorter = new DataGridSorter();
    this.datagridEvent = new EventEmitter();

    this.disableNextPageButton = false;
    this.disableLastPageButton = false;
    this.disableFirstPageButton = false;
    this.disablePreviousPageButton = false;

    this.disableFirstPageButton = true;
    this.disablePreviousPageButton = true;

    this.pageSizes.push(10);
    this.pageSizes.push(20);
    this.pageSizes.push(50);
    this.pageSizes.push(100);

    this.pageSizeForGrid = 10;

    this.sortColumn = '';
    this.sortAscending = false;
    this.sortDesending = false;

    this.executeOperation = true;
  }

  public databind = (transactionalInformation: TransactionalInformation) => {
    // OREDENAÇÂO ????
    this.currentPageNumber = transactionalInformation.currentPageNumber;
    this.totalPages = transactionalInformation.totalPages;
    this.totalRows = transactionalInformation.totalRows;

    this.itemNumberBegin = ((this.currentPageNumber - 1) * this.pageSize) + 1;
    this.itemNumberEnd = this.currentPageNumber * this.pageSize;

    if (this.itemNumberEnd > this.totalRows) {
      this.itemNumberEnd = this.totalRows;
    }

    this.disableNextPageButton = false;
    this.disableLastPageButton = false;
    this.disableFirstPageButton = false;
    this.disablePreviousPageButton = false;

    if (this.currentPageNumber === 1) {
      this.disableFirstPageButton = true;
      this.disablePreviousPageButton = true;
    }

    if (this.currentPageNumber === this.totalPages) {
      this.disableNextPageButton = true;
      this.disableLastPageButton = true;
    }
  }

  public sortData = (key: string) => {
    const sortInformation: DataGridSortInformation = this.sorter.sort(key, this.rows);

    if (this.sortColumn !== key) {
      this.sortAscending = true;
      this.sortDesending = false;
      this.sortColumn = key;
    } else {
      this.sortAscending = !this.sortAscending;
      this.sortDesending = !this.sortDesending;
    }

    const eventInformation = new DataGridEventInformation();

    eventInformation.EventType = 'Sorting';
    eventInformation.Direction = sortInformation.Direction;
    eventInformation.SortDirection = sortInformation.SortDirection;
    eventInformation.SortExpression = sortInformation.Column;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public selectedRow = (i: number) => {
    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'ItemSelected';
    eventInformation.ItemSelected = i;
    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public workingButtonState = (buttonName: string, row: any): boolean => {
    if (this.workingList) {
      const rowId = buttonName + '::' + row[row.keyProperty];
      const item = this.workingList.find(f => f === rowId);
      return item !== undefined;
    } else {
      return false;
    }
  }

  public allowDownload = (index: number): boolean => {
    const row = this.rows[index];
    return new Date(row.dataDocumento).getFullYear() >= 2015;
  }

  public allowExtracto = (index: number): boolean => {
    const row = this.rows[index];
    return row.facturacaoComExtracto;
  }

  public allowUpload = (): boolean => {
    return this.securityService.canDisplay(6004);
  }

  public allowAcompanhamento = (index: number) : boolean => {
    const row = this.rows[index];
    return row.acompanhamento;
  }

  public workingCallBack = (buttonName: string, row: any): void => {
    const rowId = buttonName + '::' + row[row.keyProperty];
    const indexOf = this.workingList.indexOf(rowId);
    this.workingList.splice(indexOf, 1);
  }

  public buttonClicked(buttonName: string, index: number) {
    if (this.workingList === undefined) {
      this.workingList = new Array<any>();
    }
    this.executeOperation = true;

    const row = this.rows[index];
    const rowId = buttonName + '::' + row[row.keyProperty];
    this.workingList.push(rowId);

    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'ButtonClicked';
    eventInformation.ButtonClicked = buttonName;
    eventInformation.ItemSelected = index;
    eventInformation.CallBack = this.workingCallBack;

    this.datagridEvent.emit({
      value: eventInformation
    });

    this.executeOperation = false;
  }

  public pageSizeChanged(newPageSize: string) {
    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'PageSizeChanged';

    this.pageSize = parseInt(newPageSize, null) + 0;
    eventInformation.PageSize = this.pageSize;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public buttonNextPage() {
    const currentPageNumber = this.currentPageNumber + 1;
    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'PagingEvent';
    eventInformation.CurrentPageNumber = currentPageNumber;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public buttonPreviousPage() {
    this.currentPageNumber = this.currentPageNumber - 1;

    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'PagingEvent';
    eventInformation.CurrentPageNumber = this.currentPageNumber;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public buttonFirstPage() {
    this.currentPageNumber = 1;

    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'PagingEvent';
    eventInformation.CurrentPageNumber = this.currentPageNumber;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }

  public buttonLastPage() {
    this.currentPageNumber = this.totalPages;

    const eventInformation = new DataGridEventInformation();
    eventInformation.EventType = 'PagingEvent';
    eventInformation.CurrentPageNumber = this.currentPageNumber;

    this.datagridEvent.emit({
      value: eventInformation
    });
  }
}
